import {keepPreviousData} from "@tanstack/react-query";
import type {GridFilterModel, GridSortModel} from "@mui/x-data-grid-premium";
import {BaseMuixApi} from "~/components/muix-data-grid/BaseMuixApi";
import {EMPTY_FILTER} from "~/components/muix-data-grid/MuixDataGridConstants";
import {flattenRelatedDataFields} from "~/services/ApiHelpers";
import type {NestedRelatedDataModel} from "~/services/ApiHelpers";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {complianceRequirementGridUrl} from "~/pages/supplier-360/services/supplier360Urls";
import type {ComplianceRequirementGridRow} from "~/pages/supplier-360/types/Supplier360Types";

/**
 * The BU fields the Applies to, Qualification and Layer columns render — expanded server-side
 * (`validKeys = ['bu_qualification', 'business_unit']`, §2.8a) instead of joined client-side. Each
 * flat key also makes its field filterable / sortable (`region` → `bu_qualification__region`).
 *
 * `business_unit` nests two hops down (`bu_qualification.business_unit.bu_name`, 2026-10-28, per the
 * user) for the Layer column's "which BU" line — `business_unit` became a FK 2026-10-13, so
 * `bu_qualification`'s own flat expansion only ever returns its id, not its name.
 */
const RELATED_DATA: NestedRelatedDataModel = {
    bu_qualification: [
        'region', 'category', 'qualification_status', 'requalification_due',
        {business_unit: ['bu_name']},
    ],
};

/** A `Date` as the user's local calendar day, `YYYY-MM-DD` — not `toISOString()`, which is UTC and can shift the day. */
function toLocalIsoDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The grid's date filters hold `Date` objects, which serialize to an ISO datetime. This grid's
 * date columns (`expires_at`, `requalification_due`) are Django `DateField`s, which reject that
 * (`… must be in YYYY-MM-DD format`) — so send the calendar day instead.
 */
function withDateOnlyValues(filterModel: GridFilterModel): GridFilterModel {
    return {
        ...filterModel,
        items: filterModel.items.map(item =>
            item.value instanceof Date ? {...item, value: toLocalIsoDate(item.value)} : item,
        ),
    };
}

/** `{"rows": [...], "rowCount": N}` — the shape every `BaseMuixViewSet.grid()` returns. */
type ComplianceRequirementsGridResponse = {
    rows: ComplianceRequirementGridRow[];
    rowCount: number;
};

export type ComplianceRequirementsGridQueryVariables = {
    entityId: number;
    /** `undefined` while the databag is still resolving — the query stays disabled until it isn't. */
    databagId: number | undefined;
    /** 1-based, as the endpoint wants it. */
    page: number;
    pageSize: number;
    sortModel: GridSortModel;
    filterModel: GridFilterModel;
};

/**
 * One page of a supplier's compliance records — `GET supplier_360/compliance_requirement/muix/grid/`
 * (docs §2.8a). Same pattern as `useSupplierTransactionsQuery`: params from the static
 * `BaseMuixApi.getMuiXQuery()` plus this endpoint's own `entity`, and the grid state from
 * `useDataGridQueryState` — every page / sort / filter change is a new query key.
 *
 * `related_bu_qualification` is flattened onto each row (`region`, `category`, …), so a column
 * reads the field directly — no client-side join.
 */
export function useComplianceRequirementsGridQuery({
                                                       entityId,
                                                       databagId,
                                                       page,
                                                       pageSize,
                                                       sortModel,
                                                       filterModel,
                                                   }: ComplianceRequirementsGridQueryVariables) {
    const url = complianceRequirementGridUrl();
    const params = databagId === undefined ? undefined : BaseMuixApi.getMuiXQuery(databagId, {
        sortModel,
        // The endpoint requires the full MUIX filter shape, not just the grid's `items`.
        filterModel: {...EMPTY_FILTER, ...withDateOnlyValues(filterModel)},
        relatedData: RELATED_DATA,
        page,
        pageSize,
    });
    params?.set('entity', String(entityId));

    return useCustomQuery({
        queryKey: [url, {params: params?.toString()}] as const,
        queryFn: async ({signal}) => {
            const {data} = await queryHttp.get<ComplianceRequirementsGridResponse>(url, {params, signal});
            return {
                rowCount: data.rowCount,
                rows: data.rows.map(row => {
                    flattenRelatedDataFields(row, RELATED_DATA);
                    return row;
                }),
            };
        },
        enabled: Boolean(entityId) && databagId !== undefined,
        // Paging and filtering keep the current page on screen instead of emptying the grid.
        placeholderData: keepPreviousData,
        showErrorNotification: true,
    });
}
